import { Badge, cardClass, EmptyState, SkeletonRows, Table, Tabs } from '@agentic/ui-kit';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { z } from 'zod';
import { AutonomyChip, SideEffectChip } from '../../../app/chips.tsx';
import { agentsQuery, modelsQuery, toolsQuery } from '../../../app/queries.ts';
import { QueryError } from '../../../app/route-states.tsx';
import { PAGE, PageHeader } from '../../../app/shell.tsx';
import { Availability, KillSwitchControl, pauseHelpText } from '../../../features/registry/kill-switch.tsx';

const Search = z.object({ tab: z.enum(['agents', 'tools', 'models']).optional().catch(undefined) });

export const Route = createFileRoute('/_app/registry/')({
  validateSearch: Search,
  component: RegistryPage,
});

function RegistryPage() {
  const { tab = 'agents' } = Route.useSearch();
  const navigate = useNavigate({ from: '/registry/' });
  return (
    <>
      <PageHeader
        title="Registry"
        eyebrow="Agents & tools"
        description="Admitted agents, the tool catalog and model profiles. Pausing an agent or tool takes effect immediately and is audited."
      />
      <div className={PAGE}>
        <div className={cardClass({ padding: 'md' })}>
          <Tabs
            label="Registry"
            value={tab}
            onValueChange={(v) =>
              void navigate({ search: { tab: v as 'agents' }, replace: true, resetScroll: false })
            }
            items={[
              { value: 'agents', label: 'Agents', content: <AgentsTable /> },
              { value: 'tools', label: 'Tools', content: <ToolsTable /> },
              { value: 'models', label: 'Model profiles', content: <ModelsTable /> },
            ]}
          />
        </div>
      </div>
    </>
  );
}

function AgentsTable() {
  const q = useQuery(agentsQuery);
  if (q.isPending) return <SkeletonRows label="Loading agents" />;
  if (q.isError) return <QueryError error={q.error} onRetry={() => void q.refetch()} />;
  if (q.data.items.length === 0)
    return <EmptyState title="No agents admitted">Enable a pack on the Modules page.</EmptyState>;
  return (
    <div className="flex flex-col gap-3">
      <p id="agents-pause-help" className="text-[13px] text-ink-2">
        {pauseHelpText('agents')}
      </p>
      <div className="-mx-2 overflow-hidden">
        <Table aria-label="Agents">
          <thead>
            <tr>
              <th scope="col">Agent</th>
              <th scope="col">Availability</th>
              <th scope="col">Operator control</th>
              <th scope="col">Domain</th>
              <th scope="col">Ceiling</th>
              <th scope="col">Model profile</th>
              <th scope="col">Prompt</th>
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((a) => (
              <tr key={a.definition.metadata.name}>
                <td>
                  <Link
                    to="/registry/agents/$name"
                    params={{ name: a.definition.metadata.name }}
                    className="font-mono text-[13px] font-medium text-ink hover:underline"
                  >
                    {a.definition.metadata.name}
                  </Link>
                  <div className="font-mono text-[12px] text-ink-3">
                    v{a.definition.metadata.version} · {a.module}
                  </div>
                </td>
                <td>
                  <Availability status={a.status} killSwitch={a.killSwitch} reason={a.reason} />
                </td>
                <td>
                  <KillSwitchControl
                    kind="agents"
                    name={a.definition.metadata.name}
                    engaged={a.killSwitch}
                    describedBy="agents-pause-help"
                  />
                </td>
                <td className="text-ink-2">{a.definition.metadata.domain}</td>
                <td>
                  <AutonomyChip value={a.definition.spec.autonomy} />
                </td>
                <td className="font-mono text-[12.5px]">{a.definition.spec.modelProfile}</td>
                <td className="font-mono text-[12.5px]">{a.definition.spec.prompt.version}</td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

function ToolsTable() {
  const q = useQuery(toolsQuery);
  if (q.isPending) return <SkeletonRows label="Loading tools" />;
  if (q.isError) return <QueryError error={q.error} onRetry={() => void q.refetch()} />;
  if (q.data.items.length === 0) return <EmptyState title="Tool catalog is empty" />;
  return (
    <div className="flex flex-col gap-3">
      <p id="tools-pause-help" className="text-[13px] text-ink-2">
        {pauseHelpText('tools')}
      </p>
      <div className="-mx-2 overflow-hidden">
        <Table aria-label="Tools">
          <thead>
            <tr>
              <th scope="col">Tool</th>
              <th scope="col">Availability</th>
              <th scope="col">Operator control</th>
              <th scope="col">Purpose</th>
              <th scope="col">Autonomy</th>
              <th scope="col">Side effect</th>
              <th scope="col">Approval</th>
              <th scope="col">Idempotency / retry</th>
            </tr>
          </thead>
          <tbody>
            {q.data.items.map(({ entry, module, killSwitch, status, reason }) => (
              <tr key={entry.name}>
                <td>
                  <code className="font-mono text-[13px] font-medium">{entry.name}</code>
                  <div className="font-mono text-[12px] text-ink-3">
                    v{entry.version} · {module} · {entry.provenance.kind}
                  </div>
                </td>
                <td>
                  <Availability status={status} killSwitch={killSwitch} reason={reason} />
                </td>
                <td>
                  <KillSwitchControl
                    kind="tools"
                    name={entry.name}
                    engaged={killSwitch}
                    describedBy="tools-pause-help"
                  />
                </td>
                <td className="max-w-[20rem] text-[13px] text-ink-2">{entry.purpose}</td>
                <td>
                  <AutonomyChip value={entry.autonomyClass} />
                </td>
                <td>
                  <SideEffectChip value={entry.sideEffectClass} />
                </td>
                <td className="font-mono text-[12.5px]">{entry.approvalPolicy}</td>
                <td className="font-mono text-[12px] text-ink-2">
                  {entry.idempotency} / {entry.retryClass}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

function ModelsTable() {
  const q = useQuery(modelsQuery);
  if (q.isPending) return <SkeletonRows label="Loading model profiles" />;
  if (q.isError) return <QueryError error={q.error} onRetry={() => void q.refetch()} />;
  // 23: the effective configuration projection (active version, concrete model, approval) when the server has it.
  const effective = new Map((q.data.effective ?? []).map((e) => [e.name, e]));
  return (
    <div className="flex flex-col gap-3">
      <p className="text-[13px] text-ink-2">
        {effective.size
          ? 'The model each profile actually sends, from its active version. '
          : 'Profiles from the deployment configuration. '}
        <Link
          to="/settings/models"
          className="font-medium underline underline-offset-2"
          data-testid="registry-models-link"
        >
          Manage model profiles
        </Link>
      </p>
      <div className="-mx-2 overflow-hidden">
        <Table aria-label="Model profiles">
          <thead>
            <tr>
              <th scope="col">Profile</th>
              <th scope="col">Adapter</th>
              <th scope="col">Model</th>
              {effective.size > 0 && <th scope="col">Version / status</th>}
              <th scope="col">Region / data</th>
              <th scope="col">Latency</th>
              <th scope="col">Fallback</th>
            </tr>
          </thead>
          <tbody>
            {q.data.items.map((m) => {
              const e = effective.get(m.name);
              return (
                <tr key={m.name}>
                  <td>
                    <code className="font-mono text-[13px] font-medium">{m.name}</code>
                    {m.name === q.data.default && (
                      <Badge tone="info" className="ml-2">
                        default
                      </Badge>
                    )}
                    {m.consequential && (
                      <div className="text-[12px] text-ink-3">consequential: never falls back silently</div>
                    )}
                  </td>
                  <td className="font-mono text-[12.5px]">{e?.adapter ?? m.adapter}</td>
                  <td className="font-mono text-[12.5px]">
                    {e?.modelId ?? m.model}
                    {e && <div className="text-[12px] text-ink-3">{e.connectionId}</div>}
                  </td>
                  {effective.size > 0 && (
                    <td className="text-[12.5px]">
                      {e ? (
                        <span className="flex flex-col gap-0.5">
                          <span className="font-mono">
                            {e.activeVersion ? `v${e.activeVersion}` : 'no active version'}
                          </span>
                          <span>
                            {!e.enabled ? (
                              <Badge tone="neutral">inactive</Badge>
                            ) : e.approvalState === 'approved' ? (
                              <Badge tone="ok">approved</Badge>
                            ) : (
                              <Badge tone="bad">model {e.approvalState}</Badge>
                            )}
                          </span>
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  )}
                  <td className="text-[12.5px] text-ink-2">
                    {m.region} · {m.dataPolicy}
                  </td>
                  <td className="font-mono text-[12.5px]">{m.latencyClass}</td>
                  <td className="font-mono text-[12px] text-ink-2">
                    {e
                      ? e.consequential
                        ? 'none'
                        : e.fallbackNames.join(' → ') || 'none'
                      : m.fallbackEligible
                        ? m.fallbackProfiles.join(' → ') || 'eligible'
                        : 'none'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    </div>
  );
}
